import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs';
import { ContextSelectorService } from './context-selector.service';
import { CurrentUserService } from './current-user.service';
import { CurrentUser } from './domain/current-user.models';
import { CurrentProfileContext, SelectedContext } from './domain/profile-context.models';
import { ProfileContextService } from './profile-context.service';

/**
 * Decides whether a resolved-to-null context is a recoverable bad pointer for this user, or an expected absence.
 * Apps provide it: agent apps require a profile for non-Consumers; consumer-facing apps exempt Consumers.
 */
export const PROFILE_REQUIRED_FOR_USER = new InjectionToken<(user: CurrentUser) => boolean>('PROFILE_REQUIRED_FOR_USER');

interface FallbackState {
  resolvedContext: CurrentProfileContext | null;
  user: CurrentUser | null;
  selectedContext: SelectedContext | undefined;
}

interface UnresolvedProfile extends FallbackState {
  resolvedContext: null;
  user: CurrentUser;
  selectedContext: SelectedContext;
}

/**
 * ContextFallbackService
 *
 * The shared "no resolvable profile -> reset to default" recovery policy. The kernel surfaces an unresolvable profile
 * as a plain null; this service decides — via the injected {@link PROFILE_REQUIRED_FOR_USER} predicate — when that null
 * is a bad pointer worth resetting versus an expected absence (e.g. a Consumer who has no profile).
 */
@Injectable({
  providedIn: 'root',
})
export class ContextFallbackService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly contextSelector = inject(ContextSelectorService);
  private readonly profileContext = inject(ProfileContextService);
  private readonly profileRequired = inject(PROFILE_REQUIRED_FOR_USER);
  private readonly destroyRef = inject(DestroyRef);

  public initialise(): void {
    // The trigger is the kernel emitting a resolved value; user and selectedContext are sampled. Using withLatestFrom instead of
    // combineLatest avoids a spurious reset during the window where selectedContext has changed but the kernel has not yet
    // produced a fresh resolution for it.
    this.profileContext.currentProfileContext$
      .pipe(
        withLatestFrom(this.currentUserService.currentUser$, this.contextSelector.selectedContext$),
        map(([resolvedContext, user, selectedContext]): FallbackState => ({ resolvedContext, user, selectedContext })),
        // The kernel emits null for three cases (no user, no selected context, fetch error); only the last is recoverable, which
        // is why this guard requires a selectedContext and a user the app's predicate says must have a profile before treating
        // null as a bad pointer.
        filter((state): state is UnresolvedProfile => this.isUnresolvedProfile(state)),
        // De-dupe so we don't re-reset for the same (user, failed pointer) we already bounced off. This does NOT guard against a
        // default that itself fails to resolve — that pointer differs from the original, so it passes through here; the actual
        // loop-stopper for that case is ProfileContextService's objectDistinctUntilChanged. Keying on userId lets a fresh
        // sign-in recover even if the new user inherits a previously-failed pointer from local storage.
        distinctUntilChanged(
          (a, b) =>
            a.user.userId === b.user.userId &&
            a.selectedContext.profileType === b.selectedContext.profileType &&
            a.selectedContext.profileCode === b.selectedContext.profileCode,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ user }) => this.contextSelector.setDefaultContext(user));
  }

  private isUnresolvedProfile(state: FallbackState): state is UnresolvedProfile {
    return state.resolvedContext === null && state.user !== null && this.profileRequired(state.user) && state.selectedContext !== undefined;
  }
}
